import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Copy, 
  QrCode, 
  Link as LinkIcon, 
  Trash2, 
  Users, 
  Calendar, 
  Share,
  Eye,
  EyeOff,
  Check,
  X,
  ExternalLink
} from 'lucide-react';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import QRCodeShare from '../QRCodeShare';

function InviteManager({ streamId = null }) {
  const { user, canManageInvitations } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState({});
  const [createForm, setCreateForm] = useState({
    type: 'general', // 'general', 'stream', 'organization'
    role: 'viewer',
    maxUses: 1,
    expiresIn: '24h',
    description: '',
    streamId: streamId || '',
    requireApproval: false,
  });

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      const data = await authService.getInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
      // Load mock data for development
      setInvitations(getMockInvitations());
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    
    try {
      const invitation = await authService.createInvitation(createForm);
      setInvitations(prev => [invitation, ...prev]);
      setShowCreateModal(false);
      setCreateForm({
        type: 'general',
        role: 'viewer',
        maxUses: 1,
        expiresIn: '24h',
        description: '',
        streamId: streamId || '',
        requireApproval: false,
      });
    } catch (error) {
      console.error('Failed to create invitation:', error);
      // Create mock invitation for development
      const mockInvite = createMockInvitation(createForm);
      setInvitations(prev => [mockInvite, ...prev]);
      setShowCreateModal(false);
    }
  };

  const handleRevokeInvitation = async (inviteCode) => {
    try {
      await authService.revokeInvitation(inviteCode);
      setInvitations(prev => prev.filter(inv => inv.code !== inviteCode));
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
    }
  };

  const copyToClipboard = async (text, inviteId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(prev => ({ ...prev, [inviteId]: true }));
      setTimeout(() => {
        setCopyFeedback(prev => ({ ...prev, [inviteId]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const shareInvitation = async (invitation) => {
    const inviteUrl = authService.generateInviteUrl(invitation.code, invitation.streamId);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CloudStream Invitation',
          text: invitation.description || 'Join me on CloudStream!',
          url: inviteUrl,
        });
      } catch (error) {
        // Fallback to copy
        copyToClipboard(inviteUrl, invitation.id);
      }
    } else {
      copyToClipboard(inviteUrl, invitation.id);
    }
  };

  const showQRCode = (invitation) => {
    setSelectedInvite(invitation);
    setShowQRModal(true);
  };

  const getExpiryDate = (createdAt, expiresIn) => {
    const created = new Date(createdAt);
    const hours = parseInt(expiresIn.replace('h', ''));
    const expiry = new Date(created.getTime() + (hours * 60 * 60 * 1000));
    return expiry;
  };

  const isExpired = (invitation) => {
    const expiry = getExpiryDate(invitation.createdAt, invitation.expiresIn);
    return new Date() > expiry;
  };

  const getMockInvitations = () => [
    {
      id: 'inv-1',
      code: 'STREAM123',
      type: 'stream',
      role: 'viewer',
      description: 'Tech Conference Live Stream',
      streamId: 'stream-1',
      maxUses: 50,
      usedCount: 23,
      expiresIn: '24h',
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      createdBy: user?.id,
      requireApproval: false,
      isActive: true,
    },
    {
      id: 'inv-2',
      code: 'GENERAL456',
      type: 'general',
      role: 'guest',
      description: 'General platform access',
      streamId: null,
      maxUses: 10,
      usedCount: 5,
      expiresIn: '168h',
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      createdBy: user?.id,
      requireApproval: true,
      isActive: true,
    },
  ];

  const createMockInvitation = (formData) => ({
    id: `inv-${Date.now()}`,
    code: Math.random().toString(36).substr(2, 8).toUpperCase(),
    ...formData,
    usedCount: 0,
    createdAt: new Date().toISOString(),
    createdBy: user?.id,
    isActive: true,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Invitation Management</h2>
          <p className="text-gray-400">Create and manage invitations with QR codes for easy sharing</p>
        </div>
        
        {canManageInvitations() && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Invitation</span>
          </button>
        )}
      </div>

      {/* Invitations List */}
      <div className="grid gap-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-4 border-cf-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading invitations...</p>
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Invitations</h3>
            <p className="text-gray-500 mb-6">Create your first invitation to start sharing access with QR codes</p>
            {canManageInvitations() && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Invitation
              </button>
            )}
          </div>
        ) : (
          invitations.map(invitation => (
            <div key={invitation.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{invitation.code}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      invitation.type === 'stream' 
                        ? 'bg-blue-500 text-white'
                        : invitation.type === 'organization'
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-500 text-white'
                    }`}>
                      {invitation.type}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isExpired(invitation)
                        ? 'bg-red-500 text-white'
                        : invitation.isActive
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-500 text-white'
                    }`}>
                      {isExpired(invitation) ? 'Expired' : invitation.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {invitation.description && (
                    <p className="text-gray-400 mb-2">{invitation.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Role</p>
                      <p className="text-white capitalize">{invitation.role}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Usage</p>
                      <p className="text-white">{invitation.usedCount} / {invitation.maxUses}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Expires</p>
                      <p className="text-white">
                        {getExpiryDate(invitation.createdAt, invitation.expiresIn).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Approval</p>
                      <p className="text-white">
                        {invitation.requireApproval ? 'Required' : 'Not Required'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                <button
                  onClick={() => copyToClipboard(authService.generateInviteUrl(invitation.code, invitation.streamId), invitation.id)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  {copyFeedback[invitation.id] ? (
                    <>
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copy Link</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => showQRCode(invitation)}
                  className="btn-secondary flex items-center space-x-2 bg-cf-orange hover:bg-orange-600 text-white"
                >
                  <QrCode className="w-4 h-4" />
                  <span>QR Code</span>
                </button>
                
                <button
                  onClick={() => shareInvitation(invitation)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Share className="w-4 h-4" />
                  <span>Share</span>
                </button>
                
                {canManageInvitations() && (
                  <button
                    onClick={() => handleRevokeInvitation(invitation.code)}
                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Invitation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Create New Invitation</h3>
            
            <form onSubmit={handleCreateInvitation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Invitation Type
                </label>
                <select
                  value={createForm.type}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="general">General Access</option>
                  <option value="stream">Specific Stream</option>
                  <option value="organization">Organization</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Role
                </label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="guest">Guest</option>
                  <option value="viewer">Viewer</option>
                  <option value="host">Host</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="What is this invitation for?"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Max Uses
                  </label>
                  <input
                    type="number"
                    value={createForm.maxUses}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, maxUses: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    min="1"
                    max="1000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Expires In
                  </label>
                  <select
                    value={createForm.expiresIn}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, expiresIn: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  >
                    <option value="1h">1 Hour</option>
                    <option value="24h">24 Hours</option>
                    <option value="168h">1 Week</option>
                    <option value="720h">30 Days</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requireApproval"
                  checked={createForm.requireApproval}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, requireApproval: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="requireApproval" className="text-sm text-gray-300">
                  Require manual approval
                </label>
              </div>
              
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Create Invitation
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enhanced QR Code Modal */}
      {showQRModal && selectedInvite && (
        <QRCodeShare
          url={authService.generateInviteUrl(selectedInvite.code, selectedInvite.streamId)}
          title={`CloudStream Invitation - ${selectedInvite.code}`}
          description={selectedInvite.description || 'Join the stream'}
          streamId={selectedInvite.streamId}
          hostName={user?.displayName}
          onClose={() => setShowQRModal(false)}
        />
      )}
    </div>
  );
}

export default InviteManager; 